import { ShieldCheck, BedDouble, Heart, Stethoscope, Layers, Sparkles, Wind, Activity, ArrowRight, ExternalLink } from 'lucide-react';
import { Reveal } from '@/components/ui/Reveal';
import CPAMBadge from '@/components/ui/CPAMBadge';

const EQUIPMENTS = [
  {
    icon: Wind,
    name: 'HoverJack®',
    tag: 'Matelas à coussin d\'air',
    description: "Matelas de transfert à coussin d'air pour un relevage sécurisé en cas de chute ou de transfert au sol. Limite les manipulations, améliore le confort patient et réduit considérablement le risque de blessure pour les soignants.",
    benefits: ['Relevage en douceur', 'Manipulation réduite', 'Confort patient']
  },
  {
    icon: Layers,
    name: 'Matériel bariatrique',
    tag: 'Jusqu\'à 500 kg',
    description: "Brancards renforcés, sangles spécifiques, dispositifs de levage adaptés et véhicules aménagés pour le transport de patients en surcharge pondérale en toute dignité et sécurité.",
    benefits: ['Charge jusqu\'à 500 kg', 'Dignité absolue', 'Équipes formées']
  },
  {
    icon: Activity,
    name: 'Chaise portoir',
    tag: 'Escaliers & espaces restreints',
    description: "Chaise de transport ergonomique permettant de descendre ou monter un patient dans les escaliers ou les espaces étroits avec un maximum de sécurité.",
    benefits: ['Escaliers étroits', 'Position assise sûre', 'Manœuvres précises']
  },
  {
    icon: BedDouble,
    name: 'Brancard moderne',
    tag: 'Réglable & ergonomique',
    description: "Brancards à hauteur variable, dossier inclinable, suspension intégrée, équipés de sangles 5 points pour une immobilisation parfaite du patient pendant le transport.",
    benefits: ['Confort optimal', 'Immobilisation 5 points', 'Mobilité fluide']
  },
  {
    icon: Stethoscope,
    name: 'Matériel d\'urgence',
    tag: 'DSA, oxygène, monitoring',
    description: "Défibrillateur Semi-Automatique (DSA), bouteilles d'oxygène, oxymètre, kit d'aspiration, équipement de surveillance vitale embarqué dans chaque véhicule de soins.",
    benefits: ['DSA embarqué', 'Oxygène disponible', 'Surveillance constante']
  },
  {
    icon: Heart,
    name: 'Lit hospitalier',
    tag: 'Transport allongé long séjour',
    description: "Véhicules aménagés pour transporter un patient directement dans son lit hospitalier, assurant la continuité des soins lors des transferts inter-établissements.",
    benefits: ['Continuité des soins', 'Pas de transfert', 'Confort maximal']
  },
];

export default function EquipementsModernesSection() {
  return (
    <section className="section-pad relative overflow-hidden" data-testid="equipements-modernes-section">
      {/* Decorative background */}
      <div className="absolute inset-0 bg-gradient-to-b from-anthracite-100/60 via-white to-white pointer-events-none"></div>
      <div className="absolute -top-32 right-0 w-[500px] h-[500px] rounded-full bg-brand-50/40 blur-3xl pointer-events-none"></div>

      <div className="container-app relative">
        <Reveal>
          <div className="max-w-3xl">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <p className="badge-soft"><Sparkles className="h-3.5 w-3.5" /> Équipements modernes</p>
              <CPAMBadge variant="solid" size="sm" />
            </div>
            <h2 className="h2 text-anthracite-950">Technologies modernes pour le confort et la sécurité des patients</h2>
            <p className="lead mt-4">
              Nos équipes utilisent des équipements modernes permettant des <strong className="text-anthracite-900 font-semibold">transferts plus sûrs, plus confortables</strong> et limitant les risques pour les patients comme pour les soignants.
            </p>
          </div>
        </Reveal>

        {/* Phrase forte */}
        <Reveal delay={120}>
          <div className="mt-8 rounded-2xl border bg-gradient-to-r from-brand-50 via-white to-brand-50 p-5 sm:p-6 flex items-start gap-4">
            <div className="shrink-0 h-12 w-12 rounded-xl bg-brand-500 text-white flex items-center justify-center">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <p className="text-anthracite-900 text-base sm:text-lg font-heading font-medium leading-snug">
              Une <span className="text-brand-700">flotte moderne</span> associée à des <span className="text-brand-700">équipements spécialisés</span> pour garantir confort, sécurité et qualité de prise en charge.
            </p>
          </div>
        </Reveal>

        {/* Grid des équipements */}
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {EQUIPMENTS.map((eq, i) => (
            <Reveal key={eq.name} delay={i * 60}>
              <article className="card-base p-6 h-full flex flex-col group relative overflow-hidden" data-testid={`equipment-card-${eq.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                {/* Decorative gradient on hover */}
                <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-brand-50 opacity-0 group-hover:opacity-100 transition-opacity blur-xl"></div>

                <div className="relative flex items-center justify-between mb-3">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-brand-50 to-brand-100 text-brand-700 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <eq.icon className="h-6 w-6" />
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-brand-700 font-medium bg-brand-50 px-2 py-1 rounded">{eq.tag}</span>
                </div>
                <h3 className="relative text-lg font-heading font-semibold text-anthracite-950">{eq.name}</h3>
                <p className="relative text-sm text-anthracite-700 leading-relaxed mt-2 flex-1">{eq.description}</p>
                <ul className="relative mt-4 pt-4 border-t border-anthracite-200/60 space-y-1">
                  {eq.benefits.map(b => (
                    <li key={b} className="flex items-center gap-2 text-xs text-anthracite-700">
                      <span className="h-1.5 w-1.5 rounded-full bg-brand-500"></span>
                      {b}
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>
          ))}
        </div>

        {/* HoverJack focus + référence */}
        <Reveal delay={120}>
          <div className="mt-10 rounded-2xl border bg-white shadow-card p-6 sm:p-8 grid lg:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <p className="text-xs uppercase tracking-wider text-brand-700 font-medium">Focus équipement</p>
              <h3 className="text-2xl font-heading font-semibold text-anthracite-950 mt-1">HoverJack® — Le relevage assisté nouvelle génération</h3>
              <p className="mt-3 text-anthracite-700 leading-relaxed">
                Le HoverJack® est un matelas gonflable à coussin d'air qui permet de relever un patient au sol sans aucun effort de portage. Il limite drastiquement les risques de blessure pour les soignants et améliore considérablement le confort du patient. Indispensable pour les chutes à domicile ou les transferts complexes.
              </p>
              <a
                href="https://www.etac.com/fr-fr/france/products/patient-handling/air-assisted-transfer/hoverjack/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-brand-700 hover:text-brand-900"
                data-testid="hoverjack-link"
              >
                Voir le HoverJack® sur ETAC <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
            <div className="shrink-0 grid grid-cols-3 lg:grid-cols-1 gap-3 text-center">
              <div className="px-4 py-3 rounded-xl bg-brand-50 border border-brand-100">
                <p className="text-2xl font-heading font-bold text-brand-800">0</p>
                <p className="text-[10px] uppercase tracking-wider text-anthracite-700">Portage manuel</p>
              </div>
              <div className="px-4 py-3 rounded-xl bg-brand-50 border border-brand-100">
                <p className="text-2xl font-heading font-bold text-brand-800">100%</p>
                <p className="text-[10px] uppercase tracking-wider text-anthracite-700">Sécurité</p>
              </div>
              <div className="px-4 py-3 rounded-xl bg-brand-50 border border-brand-100">
                <p className="text-2xl font-heading font-bold text-brand-800">↓</p>
                <p className="text-[10px] uppercase tracking-wider text-anthracite-700">Risque blessure</p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
